import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../context/LanguageContext';

const { FiList, FiClock, FiActivity, FiTrendingUp, FiTrendingDown, FiMapPin } = FiIcons;

const ProgramDetails = ({ workoutProgram }) => {
  const { t } = useLanguage();

  if (!workoutProgram) return null;

  const phases = workoutProgram.phases || [];

  const getPhaseIcon = (name) => {
    switch(name) {
      case 'warmup': return FiTrendingUp;
      case 'intense': return FiActivity;
      case 'cooldown': return FiTrendingDown;
      default: return FiClock;
    }
  };

  const getPhaseColor = (name) => {
    switch(name) {
      case 'warmup': return 'text-amber-500 bg-amber-50';
      case 'intense': return 'text-rose-500 bg-rose-50';
      case 'cooldown': return 'text-blue-500 bg-blue-50';
      default: return 'text-slate-500 bg-slate-50';
    }
  };

  const getPhaseName = (name) => {
    switch(name) {
      case 'warmup': return t('warmup');
      case 'intense': return t('workout');
      case 'cooldown': return t('cooldown');
      default: return name;
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 h-full">
      <div className="flex items-center gap-2 mb-6">
        <SafeIcon icon={FiList} className="text-blue-500 text-xl" />
        <h3 className="text-lg font-bold text-slate-800">{t('structure')}</h3>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 text-center">
          <div className="text-2xl font-bold text-blue-700">{workoutProgram.distance_km}</div>
          <div className="text-xs font-medium text-blue-600/80 uppercase tracking-wider">{t('est_km')}</div>
        </div>
        <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-100 text-center">
          <div className="text-2xl font-bold text-indigo-700">{workoutProgram.stepsTarget}</div>
          <div className="text-xs font-medium text-indigo-600/80 uppercase tracking-wider">{t('target_steps')}</div>
        </div>
      </div>

      {/* Timeline */}
      <div className="relative pl-4 space-y-6 before:absolute before:left-[19px] before:top-4 before:bottom-4 before:w-0.5 before:bg-slate-100">
        {phases.map((phase, idx) => (
          <motion.div 
            key={idx}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="relative flex gap-4"
          >
            <div className={`
              w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-4 border-white shadow-sm
              ${getPhaseColor(phase.name)}
            `}>
              <SafeIcon icon={getPhaseIcon(phase.name)} className="text-lg" />
            </div>
            
            <div className="flex-1 pt-1">
              <div className="flex justify-between items-baseline mb-1">
                <h4 className="font-bold text-slate-800 capitalize text-sm">
                  {getPhaseName(phase.name)}
                </h4>
                <span className="text-xs font-bold text-slate-400">{phase.duration_min} {t('min')}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded">
                  <SafeIcon icon={FiActivity} className="text-slate-400" /> 
                  {phase.startSpeed_kmh === phase.endSpeed_kmh ? `${phase.startSpeed_kmh}` : `${phase.startSpeed_kmh}-${phase.endSpeed_kmh}`} km/h
                </span>
                <span className="flex items-center gap-1 bg-slate-50 px-2 py-1 rounded">
                  <SafeIcon icon={FiMapPin} className="text-slate-400" /> 
                  ~{phase.estimatedSteps} {t('steps').toLowerCase()}
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default ProgramDetails;